import {FC, useMemo, useRef} from 'react';

import useFilenameExtension from '../hooks/useFilenameExtension';
import useIsOverflowing from '../hooks/useIsOverflowing';
import TruncatorWrapper from '../TruncatorWrapper';
import {TruncatorProps} from '../types';

// create canvas element, and get its content
const getContext = () => {
    const fragment = document.createDocumentFragment();
    const canvas = document.createElement('canvas');
    fragment.append(canvas);

    return canvas.getContext('2d');
};

const TruncateMiddle: FC<TruncatorProps> = ({
    className,
    href,
    isOpenInNewTab,
    isRightAligned = false,
    showFilenameExtension = false,
    text: originalText,
}) => {
    const contentRef = useRef<HTMLDivElement | null>(null);
    const {containerWidth} = useIsOverflowing(contentRef);

    const {suffix, text} = useFilenameExtension({
        showFilenameExtension,
        text: originalText,
    });

    const truncatedText = useMemo(() => {
        if (!containerWidth || !contentRef.current) {
            return text;
        }

        const context = getContext();

        if (!context) {
            return text;
        }

        const computedStyles = window.getComputedStyle(contentRef.current);
        context.font =
            computedStyles.font ??
            `${computedStyles.fontSize}" "${computedStyles.fontFamily}`;

        const textWidth = context.measureText(text).width;
        const suffixWidth = context.measureText(suffix).width;
        const availableWidth = containerWidth - suffixWidth;

        if (textWidth < availableWidth) {
            return text;
        }

        let prefixText = ''; // char from start
        let suffixText = ''; // char from end
        let indexFromLeft = 0;
        let indexFromRight = text.length - 1;
        let currentText = '...';
        let previousText = currentText;

        while (indexFromLeft < indexFromRight) {
            // Add 1 character to prefix
            prefixText += text.charAt(indexFromLeft);
            currentText = `${prefixText}...${suffixText}`;

            if (context.measureText(currentText).width > availableWidth) {
                return previousText;
            }
            previousText = currentText;

            // Add 1 character to suffix
            suffixText = text.charAt(indexFromRight) + suffixText;
            currentText = `${prefixText}...${suffixText}`;

            if (context.measureText(currentText).width > availableWidth) {
                return previousText;
            }
            previousText = currentText;
            indexFromLeft += 1;
            indexFromRight -= 1;
        }

        return currentText;
    }, [containerWidth, contentRef, suffix, text]);

    return (
        <TruncatorWrapper
            className={className}
            contentRef={contentRef}
            href={href}
            isRightAligned={isRightAligned}
            message={truncatedText === text ? '' : originalText}
            target={isOpenInNewTab ? '_blank' : ''}
        >
            <span className="overflow-hidden">{truncatedText}</span>
            {suffix && <span>{suffix}</span>}
        </TruncatorWrapper>
    );
};

export default TruncateMiddle;
